import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, TestTube } from 'lucide-react';
import {
  useCreateValidationRule,
  useUpdateValidationRule,
  type ValidationRule,
  type ValidationCondition,
  type LogicOperator
} from '../../../services/queries/validationRuleQueries';
import { ValidationConditionBuilder } from './ValidationConditionBuilder';
import { ValidationRuleTester } from './ValidationRuleTester';
import { useToast } from '../../../contexts/ToastContext';

interface ValidationRuleBuilderProps {
  rule?: ValidationRule;
  objectName: string;
  objectLabel: string;
  allFields: any[];
  onClose: () => void;
}

export const ValidationRuleBuilder: React.FC<ValidationRuleBuilderProps> = ({
  rule: initialRule,
  objectName,
  objectLabel,
  allFields,
  onClose
}) => {
  const [ruleData, setRuleData] = useState<Partial<ValidationRule>>({
    name: initialRule?.name || '',
    objectName,
    description: initialRule?.description || '',
    isActive: initialRule?.isActive ?? true,
    conditions: initialRule?.conditions || [],
    logicOperator: initialRule?.logicOperator || 'AND',
    errorMessage: initialRule?.errorMessage || '',
    errorLocation: initialRule?.errorLocation || 'top',
    errorFieldId: initialRule?.errorFieldId || undefined
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [showTester, setShowTester] = useState(false);

  const createRuleMutation = useCreateValidationRule();
  const updateRuleMutation = useUpdateValidationRule();
  const { success, error } = useToast();

  useEffect(() => {
    if (initialRule) {
      setRuleData({
        name: initialRule.name,
        objectName: initialRule.objectName,
        description: initialRule.description,
        isActive: initialRule.isActive,
        conditions: initialRule.conditions,
        logicOperator: initialRule.logicOperator,
        errorMessage: initialRule.errorMessage,
        errorLocation: initialRule.errorLocation,
        errorFieldId: initialRule.errorFieldId
      });
    }
  }, [initialRule]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleConditionsChange = (conditions: ValidationCondition[], logicOperator: LogicOperator) => {
    setRuleData(prev => ({
      ...prev,
      conditions,
      logicOperator
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validation
    if (!ruleData.name) {
      error('Rule name required', 'Please enter a rule name');
      return;
    }

    if (!ruleData.conditions || ruleData.conditions.length === 0) {
      error('No conditions configured', 'Please add at least one condition');
      return;
    }

    if (!ruleData.errorMessage) {
      error('Error message required', 'Please enter an error message');
      return;
    }

    // Check if all conditions have values (except isBlank/isNotBlank)
    const invalidConditions = ruleData.conditions.filter(c => {
      const needsValue = !['isBlank', 'isNotBlank'].includes(c.operator);
      return needsValue && (!c.value || c.value === '');
    });

    if (invalidConditions.length > 0) {
      error('Incomplete conditions', 'Please provide values for all conditions');
      return;
    }

    if (ruleData.errorLocation === 'field' && !ruleData.errorFieldId) {
      error('Error field not selected', 'Please select a field for the error location');
      return;
    }

    try {
      if (initialRule) {
        await updateRuleMutation.mutateAsync({
          ruleId: initialRule.id,
          updates: ruleData
        });
      } else {
        await createRuleMutation.mutateAsync(ruleData as Omit<ValidationRule, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>);
      }
      success('Validation rule saved successfully!');
      setHasChanges(false);
      onClose();
    } catch (err: any) {
      error('Failed to save validation rule', err.message || 'Please try again');
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {initialRule ? 'Edit' : 'Create'} Validation Rule
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {objectLabel} • Define conditions and error message
            </p>
          </div>

          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                <AlertCircle className="w-4 h-4" />
                Unsaved changes
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={!hasChanges || createRuleMutation.isPending || updateRuleMutation.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {createRuleMutation.isPending || updateRuleMutation.isPending ? 'Saving...' : 'Save Rule'}
            </button>

            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Basic Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rule Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={ruleData.name}
                    onChange={(e) => {
                      setRuleData(prev => ({ ...prev, name: e.target.value }));
                      setHasChanges(true);
                    }}
                    placeholder="e.g., High Revenue Must Be Active"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={ruleData.description || ''}
                    onChange={(e) => {
                      setRuleData(prev => ({ ...prev, description: e.target.value }));
                      setHasChanges(true);
                    }}
                    placeholder="Describe when this rule should trigger..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={ruleData.isActive}
                      onChange={(e) => {
                        setRuleData(prev => ({ ...prev, isActive: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Active
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                    Only active rules are enforced when saving records
                  </p>
                </div>
              </div>
            </div>

            {/* Conditions */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Error Conditions
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                Define when this validation rule should prevent saving
              </p>

              <ValidationConditionBuilder
                conditions={ruleData.conditions || []}
                logicOperator={ruleData.logicOperator || 'AND'}
                availableFields={allFields}
                onChange={handleConditionsChange}
              />
            </div>

            {/* Error Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Error Configuration
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Error Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={ruleData.errorMessage}
                    onChange={(e) => {
                      setRuleData(prev => ({ ...prev, errorMessage: e.target.value }));
                      setHasChanges(true);
                    }}
                    placeholder="Enter the error message users will see..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Be clear and specific about why the data is invalid
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Error Location
                  </label>

                  <div className="space-y-2">
                    <label className="flex items-center p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <input
                        type="radio"
                        checked={ruleData.errorLocation === 'top'}
                        onChange={() => {
                          setRuleData(prev => ({ ...prev, errorLocation: 'top', errorFieldId: undefined }));
                          setHasChanges(true);
                        }}
                        className="w-4 h-4 text-purple-600"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          Top of page
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Show error at the top of the page
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <input
                        type="radio"
                        checked={ruleData.errorLocation === 'field'}
                        onChange={() => {
                          setRuleData(prev => ({ ...prev, errorLocation: 'field' }));
                          setHasChanges(true);
                        }}
                        className="w-4 h-4 text-purple-600 mt-0.5"
                      />
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                          Specific field
                        </div>
                        {ruleData.errorLocation === 'field' && (
                          <select
                            value={ruleData.errorFieldId || ''}
                            onChange={(e) => {
                              setRuleData(prev => ({ ...prev, errorFieldId: e.target.value }));
                              setHasChanges(true);
                            }}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="">Select field...</option>
                            {allFields.map(field => (
                              <option key={field.id} value={field.id}>
                                {field.label}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Error Message Preview */}
                {ruleData.errorMessage && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Preview
                    </label>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-800 dark:text-red-300">
                          {ruleData.errorMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Test Rule */}
            <div>
              <button
                onClick={() => setShowTester(!showTester)}
                className="w-full flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <TestTube className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-blue-900 dark:text-blue-300">
                    Test This Rule
                  </span>
                </div>
                <span className="text-sm text-blue-700 dark:text-blue-400">
                  {showTester ? 'Hide Tester' : 'Show Tester'}
                </span>
              </button>

              {showTester && (
                <div className="mt-4">
                  <ValidationRuleTester
                    rule={ruleData}
                    allFields={allFields}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
